import React from "react";
import {
  Box,
  CircularProgress,
  createStyles,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

import CardCommon from "../../../../components/card/CardCommon";
import { CustomTheme } from "../../../../types/customTheme";
import { formatDuration, formatTime } from "./historicalUtils";

/**
 * INSIGHT-HIST-12 — orders that missed their target, slowest first.
 *
 * Each order links to its timeline (INSIGHT-HIST-16), which is how a slow
 * order is investigated.
 */

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    headerCell: {
      backgroundColor: theme.palette.background.table_header_background,
      color: theme.palette.custom.orange.contrastText,
      borderBottom: "none",
    },
    cell: {
      color: theme.palette.custom.orange.contrastText,
      borderBottom: `1px solid ${theme.palette.background.entity_border}`,
    },
    title: {
      color: theme.palette.custom.orange.contrastText,
      marginBottom: "12px",
    },
    // A plain MUI Link keeps its own primary colour, which disappears against
    // the dark card. The order number must read as clearly as the numbers.
    link: {
      color: theme.palette.custom.orange.contrastText,
      textDecoration: "underline",
      cursor: "pointer",
      "&:hover": {
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

export type DelayedOrder = {
  businessDate: string;
  locationId: string;
  orderNo: string;
  stationId?: string | null;
  durationMs: number;
  targetMs: number | null;
  completedAt: string;
};

export interface DelayedOrdersTableProps {
  orders: DelayedOrder[];
  isLoading: boolean;
  onSelectOrder: (order: DelayedOrder) => void;
}

const DelayedOrdersTable: React.FunctionComponent<DelayedOrdersTableProps> = ({
  orders,
  isLoading,
  onSelectOrder,
}) => {
  const classes = useStyles();

  return (
    <CardCommon backgroundColor="entity_background">
      <Box p="20px">
        <Typography variant="h6" className={classes.title}>
          Delayed orders
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : orders.length === 0 ? (
          <Box py={2}>
            <Typography variant="body2">
              No orders missed their target for this selection.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Order</TableCell>
                <TableCell className={classes.headerCell}>Date</TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Duration
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Target
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Over by
                </TableCell>
                <TableCell className={classes.headerCell}>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow
                  key={`${order.businessDate}-${order.orderNo}-${
                    order.stationId || ""
                  }`}
                >
                  <TableCell className={classes.cell}>
                    <Link
                      component="button"
                      variant="body2"
                      className={classes.link}
                      onClick={() => onSelectOrder(order)}
                    >
                      {order.orderNo}
                    </Link>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {order.businessDate}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {formatDuration(order.durationMs)}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {formatDuration(order.targetMs)}
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {order.targetMs === null
                      ? "-"
                      : formatDuration(order.durationMs - order.targetMs)}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {formatTime(order.completedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </CardCommon>
  );
};

export default DelayedOrdersTable;
